import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { useTheme } from '../../context/ThemeContext';
import { attendanceService, classService, studentService } from '../../services/api';

const TeacherAttendance: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [students, setStudents] = useState<any[]>([]);
  const [attendanceData, setAttendanceData] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchMyClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchClassStudents(selectedClass);
    }
  }, [selectedClass, date]);

  const fetchMyClasses = async () => {
    try {
      const response = await classService.getAll();
      // Handle different response structures
      const classesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.classes || []);
      setClasses(classesData);
    } catch (error: any) {
      console.error('Failed to fetch classes:', error);
      alert('Failed to load classes');
      setClasses([]);
    }
  };

  const fetchClassStudents = async (classId: string) => {
    try {
      setLoading(true);
      // Fetch students in the class
      const studentsResponse = await studentService.getAll({ class: classId });
      setStudents(studentsResponse.data?.students || []);

      // Try to fetch existing attendance
      try {
        const attendanceResponse = await attendanceService.getClassAttendance(classId, { date });
        const existingAttendance = attendanceResponse.data;
        const attendanceMap: { [key: string]: string } = {};
        existingAttendance.forEach((record: any) => {
          attendanceMap[record.studentId] = record.status;
        });
        setAttendanceData(attendanceMap);
      } catch (error) {
        // No attendance marked yet
        setAttendanceData({});
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
      alert('Failed to load students');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (studentId: string, status: string) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const handleSubmit = async () => {
    if (!selectedClass) {
      alert('Please select a class');
      return;
    }

    if (Object.keys(attendanceData).length === 0) {
      alert('Please mark attendance for at least one student');
      return;
    }

    try {
      setLoading(true);
      const records = Object.entries(attendanceData).map(([studentId, status]) => ({
        studentId,
        status,
        classId: selectedClass,
        date
      }));

      await attendanceService.markAttendance({ records });
      alert('Attendance marked successfully!');
    } catch (error: any) {
      console.error('Failed to mark attendance:', error);
      alert(error.response?.data?.message || 'Failed to mark attendance');
    } finally {
      setLoading(false);
    }
  };

  const markAllPresent = () => {
    const newAttendance: { [key: string]: string } = {};
    students.forEach(student => {
      newAttendance[student.id] = 'present';
    });
    setAttendanceData(newAttendance);
  };

  return (
    <Layout title="Mark Attendance">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
        <div className="mb-6 flex justify-between items-center">
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Mark Attendance
          </h2>
          <button
            onClick={() => window.history.back()}
            className="text-blue-600 hover:text-blue-700"
          >
            ← Back to Dashboard
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Select Class
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className={`w-full px-4 py-2 rounded border ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="">Choose a class...</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name} - Section {cls.section}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className={`w-full px-4 py-2 rounded border ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
        </div>

        {selectedClass && (
          <div className="mb-4">
            <button
              onClick={markAllPresent}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Mark All Present
            </button>
          </div>
        )}

        {loading && <div className="text-center py-8">Loading...</div>}

        {!loading && selectedClass && students.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <th className={`px-4 py-3 text-left ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Roll No</th>
                    <th className={`px-4 py-3 text-left ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Student Name</th>
                    <th className={`px-4 py-3 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Present</th>
                    <th className={`px-4 py-3 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Absent</th>
                    <th className={`px-4 py-3 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Late</th>
                    <th className={`px-4 py-3 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Excused</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => {
                    const user = student.user || {};
                    const firstName = user.firstName || student.firstName || '';
                    const lastName = user.lastName || student.lastName || '';
                    const rollNumber = student.rollNumber || student.studentId || 'N/A';
                    
                    return (
                      <tr key={student.id} className={isDarkMode ? 'border-gray-700' : 'border-gray-200 border-t'}>
                        <td className={`px-4 py-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{rollNumber}</td>
                        <td className={`px-4 py-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {firstName} {lastName}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="radio"
                            name={`attendance-${student.id}`}
                            checked={attendanceData[student.id] === 'present'}
                            onChange={() => handleStatusChange(student.id, 'present')}
                            className="w-4 h-4 text-green-600"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="radio"
                            name={`attendance-${student.id}`}
                            checked={attendanceData[student.id] === 'absent'}
                            onChange={() => handleStatusChange(student.id, 'absent')}
                            className="w-4 h-4 text-red-600"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="radio"
                            name={`attendance-${student.id}`}
                            checked={attendanceData[student.id] === 'late'}
                            onChange={() => handleStatusChange(student.id, 'late')}
                            className="w-4 h-4 text-yellow-600"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="radio"
                            name={`attendance-${student.id}`}
                            checked={attendanceData[student.id] === 'excused'}
                            onChange={() => handleStatusChange(student.id, 'excused')}
                            className="w-4 h-4 text-blue-600"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Attendance'}
              </button>
            </div>
          </>
        )}

        {!loading && selectedClass && students.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No students found in this class
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TeacherAttendance;
